import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  User, Mail, Phone, Building2, Shield, Bell, Moon, Sun,
  Camera, Save, Lock, Eye, EyeOff, Check, AlertCircle,
  Globe, Clock, Palette, LogOut, Smartphone, Key, Copy, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useTheme } from '../context/ThemeContext';
import { WsafeSiSettingsCard } from '../components/WsafeSiSettingsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserProfileSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isDarkMode, toggleTheme } = useTheme();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    department: '',
    role: user?.role || 'user',
    title: '',
    company: ''
  });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    pushNotifications: true,
    incidentAlerts: true,
    complianceReminders: true,
    trainingReminders: true,
    weeklyDigest: true,
    monthlyReport: false
  });
  
  // Appearance settings
  const [appearance, setAppearance] = useState({
    compactMode: false,
    showAnimations: true,
    highContrast: false
  });

  // MFA state
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, loading: true });
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'user'
      }));
      // Fetch MFA status
      fetchMfaStatus();
    }
  }, [user]);

  const fetchMfaStatus = async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/mfa/status/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus({ enabled: response.data.mfa_enabled, loading: false });
    } catch (error) {
      setMfaStatus({ enabled: false, loading: false });
    }
  };

  const handleMfaSetup = async () => {
    if (!user?.id) return;
    setMfaLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/mfa/setup/${user.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaSetup(response.data);
      toast({ title: "MFA Setup Started", description: "Scan the QR code with your authenticator app" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to start MFA setup", variant: "destructive" });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!user?.id || !mfaCode) return;
    setMfaLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/mfa/verify-setup/${user.id}`, { code: mfaCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus({ enabled: true, loading: false });
      setMfaSetup(null);
      setMfaCode('');
      toast({ title: "MFA Enabled", description: "Two-factor authentication is now active on your account" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Invalid code", variant: "destructive" });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!user?.id) return;
    const code = prompt("Enter your current MFA code to disable:");
    const password = prompt("Enter your password to confirm:");
    if (!code || !password) return;
    
    setMfaLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/mfa/disable/${user.id}`, { code, password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus({ enabled: false, loading: false });
      toast({ title: "MFA Disabled", description: "Two-factor authentication has been removed" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to disable MFA", variant: "destructive" });
    } finally {
      setMfaLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Copied to clipboard" });
  };

  const handleProfileSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/users/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Profile updated successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update profile", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({ title: "Error", description: "Passwords do not match", variant: "destructive" });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Password changed successfully" });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to change password", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationsSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/users/notifications`, notifications, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Notification preferences saved" });
    } catch (error) {
      toast({ title: "Info", description: "Preferences saved locally" });
    } finally {
      setLoading(false);
    }
    localStorage.setItem('wsafe-notifications', JSON.stringify(notifications));
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'integrations', label: 'WSafe.SI', icon: Globe },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Settings
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Manage your account settings and preferences
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`settings-tab-${tab.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? isDarkMode 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-blue-50 text-blue-700'
                          : isDarkMode
                            ? 'text-slate-300 hover:bg-slate-700'
                            : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>

            {/* User Card */}
            <Card className={`mt-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
              <CardContent className="p-4 text-center">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-xl font-bold ${
                  isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
                }`}>
                  {profile.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <p className={`mt-3 font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {profile.name || 'User'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {profile.email}
                </p>
                <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                  isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                }`}>
                  {profile.role}
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Profile Information</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Update your personal information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Full Name</Label>
                      <Input
                        value={profile.name}
                        onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Email</Label>
                      <Input
                        type="email"
                        value={profile.email}
                        onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                      <Input
                        value={profile.phone}
                        onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Department</Label>
                      <Input
                        value={profile.department}
                        onChange={e => setProfile(p => ({ ...p, department: e.target.value }))}
                        placeholder="Safety Department"
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Job Title</Label>
                      <Input
                        value={profile.title}
                        onChange={e => setProfile(p => ({ ...p, title: e.target.value }))}
                        placeholder="Safety Manager"
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Company</Label>
                      <Input
                        value={profile.company}
                        onChange={e => setProfile(p => ({ ...p, company: e.target.value }))}
                        placeholder="Company Name"
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleProfileSave} disabled={loading}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Security Settings</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Manage your password and security preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Change Password
                    </h3>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                          className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>New Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                        className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      />
                    </div>
                    <Button onClick={handlePasswordChange} disabled={loading}>
                      <Lock className="w-4 h-4 mr-2" />
                      Update Password
                    </Button>
                  </div>

                  <div className={`pt-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h3 className={`font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Two-Factor Authentication (MFA)
                    </h3>
                    
                    {mfaStatus.loading ? (
                      <div className="flex items-center gap-2 text-slate-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Checking MFA status...
                      </div>
                    ) : mfaStatus.enabled ? (
                      <div className="space-y-4">
                        <div className={`flex items-center gap-3 p-4 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                          <Shield className="w-5 h-5 text-green-500" />
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                              MFA is Enabled
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-green-400/80' : 'text-green-600'}`}>
                              Your account is protected with two-factor authentication
                            </p>
                          </div>
                          <Badge className="ml-auto bg-green-500 text-white">Active</Badge>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={handleMfaDisable}
                          disabled={mfaLoading}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          {mfaLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                          Disable MFA
                        </Button>
                      </div>
                    ) : mfaSetup ? (
                      <div className="space-y-4">
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                          </p>
                          <div className="flex justify-center mb-4">
                            <img 
                              src={mfaSetup.qr_code} 
                              alt="MFA QR Code" 
                              className="w-48 h-48 rounded-lg bg-white p-2"
                            />
                          </div>
                          <div className="flex items-center gap-2 mb-4">
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Manual entry: <code className="bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">{mfaSetup.secret}</code>
                            </p>
                            <button onClick={() => copyToClipboard(mfaSetup.secret)} className="text-blue-500 hover:text-blue-600">
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            2. Enter the 6-digit code from your app
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="000000"
                              maxLength={6}
                              value={mfaCode}
                              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                              className={`w-32 text-center text-lg tracking-widest ${isDarkMode ? 'bg-slate-600 border-slate-500 text-white' : ''}`}
                            />
                            <Button onClick={handleMfaVerify} disabled={mfaCode.length !== 6 || mfaLoading}>
                              {mfaLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                              Verify & Enable
                            </Button>
                          </div>
                        </div>
                        
                        {/* Backup Codes */}
                        <div>
                          <button 
                            onClick={() => setShowBackupCodes(!showBackupCodes)}
                            className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                          >
                            <Key className="w-4 h-4" />
                            {showBackupCodes ? 'Hide' : 'Show'} Backup Codes
                          </button>
                          {showBackupCodes && (
                            <div className={`mt-2 p-3 rounded ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                              <p className={`text-xs mb-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                                Save these backup codes securely. Each can only be used once.
                              </p>
                              <div className="grid grid-cols-2 gap-1">
                                {mfaSetup.backup_codes?.map((code, i) => (
                                  <code key={i} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-white'}`}>
                                    {code}
                                  </code>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <Button variant="outline" onClick={() => setMfaSetup(null)}>
                          Cancel Setup
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`flex items-center gap-3 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <Smartphone className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              MFA is Not Enabled
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Add an extra layer of security to your account
                            </p>
                          </div>
                        </div>
                        <Button onClick={handleMfaSetup} disabled={mfaLoading}>
                          {mfaLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                          Enable Two-Factor Authentication
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className={`pt-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h3 className={`font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Session
                    </h3>
                    <Button variant="destructive" onClick={onLogout}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Notification Preferences</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Choose how you want to be notified
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { key: 'emailAlerts', label: 'Email Alerts', desc: 'Receive alerts via email' },
                    { key: 'pushNotifications', label: 'Push Notifications', desc: 'Browser push notifications' },
                    { key: 'incidentAlerts', label: 'Incident Alerts', desc: 'Get notified when incidents are reported' },
                    { key: 'complianceReminders', label: 'Compliance Reminders', desc: 'Reminders for upcoming deadlines' },
                    { key: 'trainingReminders', label: 'Training Reminders', desc: 'Training due date reminders' },
                    { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Weekly summary of safety activities' },
                    { key: 'monthlyReport', label: 'Monthly Report', desc: 'Monthly safety performance report' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {item.label}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {item.desc}
                        </p>
                      </div>
                      <Switch
                        checked={notifications[item.key]}
                        onCheckedChange={checked => setNotifications(n => ({ ...n, [item.key]: checked }))}
                      />
                    </div>
                  ))}
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleNotificationsSave} disabled={loading}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Preferences
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'integrations' && <WsafeSiSettingsCard />}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Appearance</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Customize how WSafe AI looks
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Dark Mode Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isDarkMode ? <Moon className="w-5 h-5 text-blue-400" /> : <Sun className="w-5 h-5 text-amber-500" />}
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Dark Mode
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {isDarkMode ? 'Currently using dark theme' : 'Currently using light theme'}
                        </p>
                      </div>
                    </div>
                    <Switch checked={isDarkMode} onCheckedChange={toggleTheme} />
                  </div>

                  <div className={`border-t pt-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Compact Mode
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Reduce spacing for more content
                        </p>
                      </div>
                      <Switch
                        checked={appearance.compactMode}
                        onCheckedChange={checked => setAppearance(a => ({ ...a, compactMode: checked }))}
                      />
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Animations
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Enable smooth transitions
                        </p>
                      </div>
                      <Switch
                        checked={appearance.showAnimations}
                        onCheckedChange={checked => setAppearance(a => ({ ...a, showAnimations: checked }))}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          High Contrast
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Increase text contrast for accessibility
                        </p>
                      </div>
                      <Switch
                        checked={appearance.highContrast}
                        onCheckedChange={checked => setAppearance(a => ({ ...a, highContrast: checked }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;
